import React, {Fragment, useEffect, useMemo, useState} from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { faCheck, faChevronDown, faTrash, faMicrophone, faDrum, faGuitar, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TinyLoader from "../loaders/TinyLoader";
import { useI18n } from '../../i18n';

const roleIcons: Record<string, any> = {
    alto: faMicrophone,
    tenor: faMicrophone,
    soprano: faMicrophone,
    bass: faMicrophone,
    drummer: faDrum,
    guitar: faGuitar,
    guitarist: faGuitar,
};

interface Props {
    items: any[];
    loading?: boolean;
    onChange?: (item: any) => void;
    handleSearch?: (query: string) => void;
    value?: any;
    type?: string;
    isStatic?: boolean;
    onRemove?: (item: any) => void;
    query: string;
    setQuery: (q: string) => void;
    placeholder?: string;
    getLabel?: (item: any) => string;
    showDeleteAction?: boolean;
}

const AutoCompleteSearchInput = ({
                                     items,
                                     loading,
                                     value,
                                     onChange,
                                     handleSearch,
                                     type,
                                     isStatic = false,
                                     onRemove,
                                     query,
                                     setQuery,
                                     placeholder,
                                     getLabel,
                                     showDeleteAction = true,
                                 }: Props) => {
    const { t } = useI18n();
    const [selected, setSelected] = useState(value ?? null);

    useEffect(() => {
        setSelected(value ?? null);
    }, [value]);

    const filteredItems = useMemo(() => {
        if (handleSearch) return items; // external search: use items directly
        if (!query || query.trim() === '') return items;

        return items.filter((item: any) =>
            (item?.name || item?.title || '')
                .toLowerCase()
                .includes(query.toLowerCase())
        );
    }, [items, query, handleSearch]);

    const groupedItems: { [key: string]: any[] } = {};
    for (const item of filteredItems) {
        const group = item.source ? t('components.autoCompleteSearch.fromSpotify') : t('components.autoCompleteSearch.fromLibrary');
        if (!groupedItems[group]) groupedItems[group] = [];
        groupedItems[group].push(item);
    }

    return (
        <div className="w-full">
            <Combobox value={selected} onChange={onChange || setSelected}>
                <div className="relative mt-1">
                    <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-ORANGE_BLACK text-left shadow-md sm:text-sm p-1">
                        <Combobox.Input
                            className="w-full bg-white capitalize dark:bg-ORANGE_BLACK border-none py-2 pl-3 pr-10 text-sm leading-5 text-neutral-400 focus:ring-0"
                            displayValue={(item: any) => getLabel?.(item) || item?.name || item?.title || ''}
                            onChange={(event) => {
                                const val = event.target.value;
                                setQuery && setQuery(val);
                                handleSearch?.(val);
                            }}
                            placeholder={placeholder}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-2 flex items-center pr-2">
                            <FontAwesomeIcon icon={faChevronDown} className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </Combobox.Button>
                    </div>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Combobox.Options className="absolute z-[100] mt-1 max-h-80 w-full overflow-auto rounded-md bg-white dark:bg-ORANGE_BLACK py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                            {loading ? (
                                <div className="px-4 py-2 text-center">
                                    <TinyLoader className={'h-auto w-auto mx-auto'} />
                                </div>
                            ) : items.length === 0 && query !== '' ? (
                                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                    {t('components.autoCompleteSearch.nothingFound')}
                                </div>
                            ) : (
                                Object.entries(groupedItems).map(([groupName, groupItems], groupIndex) => (
                                    <div key={groupName + groupIndex} className="px-2">
                                        <div className="text-xs text-gray-400 px-2 pt-2 pb-1 uppercase tracking-wide">
                                            {groupName}
                                        </div>
                                        {groupItems.map((item, index) => (
                                            <Combobox.Option
                                                key={item?.id || index}
                                                className={({ active }) =>
                                                    `relative flex justify-between items-center gap-3 cursor-pointer select-none py-2 px-4 ${
                                                        active ? 'bg-orange-600 text-white' : 'text-gray-300'
                                                    }`
                                                }
                                                value={item}
                                            >
                                                {({ selected: isSelected, active }) => (
                                                    <>
                                                        <div className="flex items-center gap-3 truncate flex-1">
                                                            {item?.song_art || item?.artist ? (
                                                                <>
                                                                    {item?.song_art && (
                                                                        <img
                                                                            src={item.song_art}
                                                                            alt={item.title}
                                                                            className="w-8 h-8 rounded object-cover"
                                                                        />
                                                                    )}
                                                                    <div className="flex flex-col">
                                                                        <span
                                                                            className="font-medium capitalize truncate">
                                                                          {getLabel?.(item) || item?.name?.replace('_', ' ') || item?.title?.replace('_', ' ')}
                                                                        </span>
                                                                        <span
                                                                            className="text-xs text-gray-400 truncate">{item?.artist}</span>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon
                                                                        icon={roleIcons[(item?.name || item?.title || '').toLowerCase()] || faUser}
                                                                        className="w-4 h-4 text-gray-400 shrink-0"
                                                                    />
                                                                    <span className="font-medium capitalize truncate">
                                                                        {item?.name?.replace('_', ' ') || item?.title?.replace('_', ' ')}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            {!isStatic && showDeleteAction && onRemove && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onRemove?.(item);
                                                                    }}
                                                                    className="focus:outline-none"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4 text-red-500 hover:text-red-700" />
                                                                </button>
                                                            )}
                                                            {isSelected && (
                                                                <FontAwesomeIcon
                                                                    icon={faCheck}
                                                                    className={`w-4 h-4 ${active ? 'text-white' : 'text-teal-600'}`}
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))}
                                    </div>
                                ))
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>
            </Combobox>
        </div>
    );
};

export default AutoCompleteSearchInput;
